import {
  flexRender,
  getCoreRowModel,
  type Column,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnPinningState,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  useReactTable
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, Bell, ExternalLink, Star } from "lucide-react";
import {
  memo,
  type CSSProperties,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef
} from "react";
import {
  formatCurrency,
  formatExchangeLabel,
  formatMaterialityBps,
  formatNumber,
  formatPercent,
  formatShortDate,
  titleCase
} from "../lib/format";
import { describeTransaction } from "../lib/labels";
import { limitScreenerSorts } from "../lib/screener-state";
import type {
  CanonicalTransaction,
  ScreenerDensity,
  ScreenerSortField
} from "../lib/types";
import { cn } from "../lib/utils";
import { SCREENER_COLUMN_LABELS } from "./TableControls";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const SORTABLE_COLUMN_IDS = new Set<ScreenerSortField>([
  "filingDate",
  "tradeDate",
  "ticker",
  "companyName",
  "insiderName",
  "value",
  "shares",
  "price",
  "ownershipChangePct",
  "materialityBps",
  "filingLagDays",
  "score",
  "confidence",
  "distinctInsiderCount"
]);
const VIRTUAL_ROW_OVERSCAN = 5;
const VIRTUAL_INITIAL_RECT = { width: 0, height: 360 };
const ROW_HEIGHT_BY_DENSITY: Record<ScreenerDensity, number> = {
  compact: 40,
  comfortable: 48
};
type TableNavigationKey = "ArrowUp" | "ArrowDown" | "Home" | "End";
type VirtualScrollAlignment = "auto" | "end" | "start";

export interface InsiderTableFocusRequest {
  requestId: number;
  transactionId: string;
}

interface PendingRowFocus {
  align: VirtualScrollAlignment;
  focusRequestId: number | null;
  transactionId: string;
}

interface InsiderTableProps {
  rows: CanonicalTransaction[];
  totalResultCount: number;
  favoriteTickers: string[];
  canUseFavorites: boolean;
  canCreateAlerts?: boolean;
  alertPendingTicker?: string | null;
  sorting: SortingState;
  pagination: PaginationState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  density: ScreenerDensity;
  selectedTransactionId: string | null;
  focusRequest: InsiderTableFocusRequest | null;
  scrollerRef: RefObject<HTMLDivElement>;
  onToggleFavorite: (ticker: string) => void;
  onCreateTickerAlert?: (ticker: string) => void;
  onSortingChange: OnChangeFn<SortingState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  onColumnPinningChange: OnChangeFn<ColumnPinningState>;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onActivate: (transactionId: string, target: HTMLElement) => void;
  onFocusRequestComplete: (requestId: number, focused: boolean) => void;
}


function updateSorting(
  current: SortingState,
  sortField: ScreenerSortField,
  multi: boolean
): SortingState {
  const existingIndex = current.findIndex((sort) => sort.id === sortField);
  if (multi) {
    return limitScreenerSorts(
      existingIndex < 0
        ? [...current, { id: sortField, desc: true }]
        : current.map((sort, index) =>
            index === existingIndex ? { ...sort, desc: !sort.desc } : sort
          )
    );
  }
  const nextSort =
    existingIndex < 0
      ? { id: sortField, desc: true }
      : { id: sortField, desc: !current[existingIndex]!.desc };
  return limitScreenerSorts([
    nextSort,
    ...current.filter((sort) => sort.id !== sortField)
  ]);
}

function pinnedStyle(column: Column<CanonicalTransaction>, header = false): CSSProperties {
  const pinned = column.getIsPinned();
  return {
    position: pinned ? "sticky" : "relative",
    left: pinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: pinned === "right" ? `${column.getAfter("right")}px` : undefined,
    zIndex: pinned ? (header ? 30 : 5) : header ? 20 : 0,
    width: column.getSize(),
    minWidth: column.columnDef.minSize,
    maxWidth: column.columnDef.maxSize,
    background: pinned ? "var(--surface)" : undefined
  };
}

function SortHeader({
  label,
  sortField,
  sorting,
  onSortingChange
}: {
  label: string;
  sortField: ScreenerSortField;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}) {
  const priority = sorting.findIndex((sort) => sort.id === sortField);
  const sort = priority >= 0 ? sorting[priority]! : null;
  const Icon = sort?.desc === false ? ArrowUp : ArrowDown;
  const multiSortPriority = sorting.length > 1 && sort ? priority + 1 : null;
  // Keep accessible name equal to contiguous visible text ("Score" or "Score 1") to satisfy
  // axe label-content-name-mismatch under multi-sort badges, while preserving Sort-by cues.
  const accessibleName =
    multiSortPriority !== null
      ? `${label} ${multiSortPriority}. Sort by ${label}, currently ${
          sort!.desc ? "descending" : "ascending"
        }`
      : sort
        ? `${label}. Sort by ${label}, currently ${sort.desc ? "descending" : "ascending"}`
        : `${label}. Sort by ${label}`;
  return (
    <button
      type="button"
      aria-label={accessibleName}
      className={cn(
        "inline-flex min-h-6 items-center gap-1 whitespace-nowrap font-semibold normal-case",
        sort && "text-foreground"
      )}
      onClick={(event) =>
        onSortingChange((current) => updateSorting(current, sortField, event.shiftKey))
      }
    >
      <span>{label}</span>
      <Icon className={cn("h-3 w-3", !sort && "opacity-30")} aria-hidden="true" />
      {multiSortPriority !== null ? (
        <span
          aria-hidden="true"
          className="inline-flex h-4 min-w-4 items-center justify-center rounded bg-surface-elevated px-1 text-[9px] text-muted-foreground"
        >
          {multiSortPriority}
        </span>
      ) : null}
      {priority > 0 && sort ? (
        <span className="sr-only">
          Sort priority {priority + 1}, {sort.desc ? "descending" : "ascending"}
        </span>
      ) : null}
    </button>
  );
}

function navigationTargetIndex(
  key: string,
  currentIndex: number,
  rowCount: number
): number | null {
  if (rowCount === 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return rowCount - 1;
  if (key === "ArrowUp") return Math.max(0, currentIndex - 1);
  if (key === "ArrowDown") return Math.min(rowCount - 1, currentIndex + 1);
  return null;
}

interface InsiderTableBodyRowProps {
  row: Row<CanonicalTransaction>;
  selected: boolean;
  density: ScreenerDensity;
  virtualIndex: number;
  logicalRowCount: number;
  measureElement: (element: HTMLTableRowElement | null) => void;
  onActivate: (transactionId: string, target: HTMLElement) => void;
  onNavigate: (targetIndex: number, key: TableNavigationKey) => void;
  onToggleFavorite: (ticker: string) => void;
  presentationVersion: object;
}

const InsiderTableBodyRow = memo(function InsiderTableBodyRow({
  row,
  selected,
  density,
  virtualIndex,
  logicalRowCount,
  measureElement,
  onActivate,
  onNavigate,
  onToggleFavorite
}: InsiderTableBodyRowProps) {
  return (
    <tr
      ref={measureElement}
      data-index={virtualIndex}
      data-transaction-id={row.original.transactionId}
      data-density={density}
      tabIndex={0}
      aria-rowindex={virtualIndex + 2}
      aria-selected={selected}
      className={cn(
        "cursor-pointer border-b border-border/70 outline-none hover:bg-surface-elevated focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
        density === "compact"
          ? "h-[var(--row-height-compact)]"
          : "h-[var(--row-height-comfortable)]",
        selected && "bg-accent-soft shadow-[inset_3px_0_0_var(--accent)]"
      )}
      onClick={(event) => {
        onActivate(row.original.transactionId, event.currentTarget);
        row.toggleSelected(true);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
          onActivate(row.original.transactionId, event.currentTarget);
          row.toggleSelected(true);
          return;
        }
        if (event.key === "f" || event.key === "F") {
          event.preventDefault();
          onToggleFavorite(row.original.ticker);
          return;
        }
        const targetIndex = navigationTargetIndex(
          event.key,
          virtualIndex,
          logicalRowCount
        );
        if (targetIndex === null) return;
        event.preventDefault();
        onNavigate(targetIndex, event.key as TableNavigationKey);
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const pinned = cell.column.getIsPinned();
        return (
          <td
            key={cell.id}
            data-column-id={cell.column.id}
            data-pinned={pinned || undefined}
            className="overflow-hidden px-2 py-1 align-middle"
            style={pinnedStyle(cell.column)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
}, (previous, next) =>
  previous.row.id === next.row.id &&
  // Canonical transactions are immutable after dataset validation, so their
  // reference is the stable render identity even when TanStack replaces Row wrappers.
  previous.row.original === next.row.original &&
  previous.selected === next.selected &&
  previous.density === next.density &&
  previous.virtualIndex === next.virtualIndex &&
  previous.logicalRowCount === next.logicalRowCount &&
  previous.measureElement === next.measureElement &&
  previous.onActivate === next.onActivate &&
  previous.onNavigate === next.onNavigate &&
  previous.onToggleFavorite === next.onToggleFavorite &&
  previous.presentationVersion === next.presentationVersion
);

export const InsiderTable = memo(function InsiderTable({
  rows,
  totalResultCount,
  favoriteTickers,
  canUseFavorites,
  canCreateAlerts = false,
  alertPendingTicker = null,
  sorting,
  pagination,
  columnVisibility,
  columnOrder,
  columnPinning,
  density,
  selectedTransactionId,
  focusRequest,
  scrollerRef,
  onToggleFavorite,
  onCreateTickerAlert,
  onSortingChange,
  onPaginationChange,
  onColumnVisibilityChange,
  onColumnOrderChange,
  onColumnPinningChange,
  onRowSelectionChange,
  onActivate,
  onFocusRequestComplete
}: InsiderTableProps) {
  const favoriteTickerSet = useMemo(
    () => new Set(favoriteTickers.map((ticker) => ticker.toUpperCase())),
    [favoriteTickers]
  );
  const sortableHeader = (sortField: ScreenerSortField, label: string) => () => (
    <SortHeader
      label={label}
      sortField={sortField}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
  const columns = useMemo<ColumnDef<CanonicalTransaction>[]>(
    () => [
      {
        id: "favorite",
        header: () => <span className="sr-only">Favorite</span>,
        size: 44,
        minSize: 44,
        maxSize: 44,
        cell: ({ row }) => {
          const ticker = row.original.ticker;
          const isFavorite = favoriteTickerSet.has(ticker.toUpperCase());
          return (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`${isFavorite ? "Remove" : "Add"} ${ticker} ${isFavorite ? "from" : "to"} favorites`}
              disabled={!canUseFavorites}
              onClick={(event) => {
                event.stopPropagation();
                onToggleFavorite(ticker);
              }}
            >
              <Star
                className={cn("h-3.5 w-3.5", isFavorite && "fill-accent text-accent")}
                aria-hidden="true"
              />
            </Button>
          );
        }
      },
      {
        id: "ticker",
        header: sortableHeader("ticker", "Ticker"),
        size: 88,
        minSize: 76,
        cell: ({ row }) => (
          <div className="max-w-[7rem] truncate font-semibold text-foreground">
            {row.original.ticker}
          </div>
        )
      },
      {
        id: "exchange",
        header: "Exchange",
        size: 92,
        cell: ({ row }) => <span>{formatExchangeLabel(row.original.exchange)}</span>
      },
      {
        id: "companyName",
        header: sortableHeader("companyName", "Company"),
        size: 190,
        minSize: 160,
        cell: ({ row }) => (
          <div
            className="max-w-[15rem] truncate font-medium"
            title={row.original.companyName}
          >
            {row.original.companyName}
          </div>
        )
      },
      {
        id: "industryDescription",
        header: "Industry",
        size: 180,
        minSize: 150,
        cell: ({ row }) => (
          <div className="max-w-[14rem] truncate" title={row.original.industryDescription}>
            {row.original.industryDescription}
          </div>
        )
      },
      {
        id: "insiderName",
        header: sortableHeader("insiderName", "Insider"),
        size: 176,
        minSize: 150,
        cell: ({ row }) => (
          <div className="max-w-[13rem] truncate" title={row.original.insiderName}>
            {row.original.insiderName}
          </div>
        )
      },
      {
        id: "relationshipFlags",
        header: "Relationship",
        size: 140,
        cell: ({ row }) => {
          const relationship = row.original.relationshipFlags.join(", ") || row.original.role;
          return (
            <div className="max-w-[11rem] truncate" title={relationship}>
              {relationship}
            </div>
          );
        }
      },
      {
        id: "signal",
        header: "Signal",
        size: 150,
        cell: ({ row }) => {
          const description = describeTransaction(row.original);
          return (
            <div className={cn("min-w-0", density === "comfortable" && "space-y-0.5")}>
              <Badge variant={description.badgeVariant}>{description.signalLabel}</Badge>
              <span
                className={cn(
                  "block truncate text-[11px] text-muted-foreground",
                  density === "compact" && "sr-only"
                )}
              >
                {description.activityLabel}
              </span>
            </div>
          );
        }
      },
      {
        id: "confidence",
        header: sortableHeader("confidence", "Confidence"),
        size: 104,
        cell: ({ row }) => <span>{titleCase(row.original.signal.confidence)}</span>
      },
      {
        id: "filingDate",
        header: sortableHeader("filingDate", "Filed"),
        size: 168,
        cell: ({ row }) => (
          <div className={cn("whitespace-nowrap", density === "comfortable" && "flex flex-col") }>
            <span title={row.original.filingDate ?? undefined}>
              {formatShortDate(row.original.filingDate)}
            </span>
            <span
              className={cn("text-muted-foreground", density === "compact" && "ml-1")}
              title={row.original.tradeDate ?? undefined}
            >
              Traded {formatShortDate(row.original.tradeDate)}
            </span>
          </div>
        )
      },
      {
        id: "tradeDate",
        header: sortableHeader("tradeDate", "Traded"),
        size: 110,
        cell: ({ row }) => (
          <span title={row.original.tradeDate ?? undefined}>
            {formatShortDate(row.original.tradeDate)}
          </span>
        )
      },
      {
        id: "filingLagDays",
        header: sortableHeader("filingLagDays", "Filing lag"),
        size: 92,
        cell: ({ row }) => <span>{formatNumber(row.original.filingLagDays)}</span>
      },
      {
        id: "shares",
        header: sortableHeader("shares", "Shares"),
        size: 112,
        cell: ({ row }) => <span>{formatNumber(row.original.shares)}</span>
      },
      {
        id: "price",
        header: sortableHeader("price", "Price"),
        size: 104,
        cell: ({ row }) => <span>{formatCurrency(row.original.price)}</span>
      },
      {
        id: "value",
        header: sortableHeader("value", "Value"),
        size: 126,
        cell: ({ row }) => (
          <div className="text-right font-medium tabular-nums">{formatCurrency(row.original.value)}</div>
        )
      },
      {
        id: "holdingsBefore",
        header: "Holdings before",
        size: 132,
        cell: ({ row }) => <span>{formatNumber(row.original.holdingsBefore)}</span>
      },
      {
        id: "holdingsAfter",
        header: "Holdings after",
        size: 132,
        cell: ({ row }) => <span>{formatNumber(row.original.holdingsAfter)}</span>
      },
      {
        id: "ownershipChangePct",
        header: sortableHeader("ownershipChangePct", "Holdings change"),
        size: 142,
        cell: ({ row }) => <span>{formatPercent(row.original.ownershipChangePct)}</span>
      },
      {
        id: "materialityBps",
        header: sortableHeader("materialityBps", "Materiality"),
        size: 118,
        cell: ({ row }) => (
          <div className="text-right tabular-nums">
            {formatMaterialityBps(row.original.marketContext.valueToMarketCapBps)}
          </div>
        )
      },
      {
        id: "distinctInsiderCount",
        header: sortableHeader("distinctInsiderCount", "Cluster insiders"),
        size: 126,
        cell: ({ row }) => <span>{formatNumber(row.original.cluster?.distinctInsiderCount ?? null)}</span>
      },
      {
        id: "transactionCode",
        header: "Code",
        size: 70,
        cell: ({ row }) => <span>{row.original.transactionCode}</span>
      },
      {
        id: "planStatus",
        header: "Plan",
        size: 110,
        cell: ({ row }) => <span>{titleCase(row.original.plan.status)}</span>
      },
      {
        id: "securityStatus",
        header: "Security",
        size: 122,
        cell: ({ row }) => <span>{titleCase(row.original.securityStatus)}</span>
      },
      {
        id: "valueQuality",
        header: "Value quality",
        size: 118,
        cell: ({ row }) => <span>{titleCase(row.original.valueQuality)}</span>
      },
      {
        id: "flags",
        header: "Flags",
        size: 170,
        cell: ({ row }) => {
          const flags = row.original.flags.join(", ") || "N/A";
          return (
            <div className="max-w-[13rem] truncate" title={flags}>
              {flags}
            </div>
          );
        }
      },
      {
        id: "score",
        header: sortableHeader("score", "Score"),
        size: 86,
        cell: ({ row }) => (
          <div className="text-right">
            <Badge variant="score">
              {row.original.signal.score === null ? "N/A" : row.original.signal.score}
            </Badge>
          </div>
        )
      },
      {
        id: "details",
        header: () => <span className="sr-only">Details and alerts</span>,
        size: 88,
        minSize: 88,
        maxSize: 96,
        cell: ({ row }) => {
          const ticker = row.original.ticker;
          const alertBusy = alertPendingTicker?.toUpperCase() === ticker.toUpperCase();
          return (
            <div className="flex items-center justify-end gap-0.5">
              {canCreateAlerts && onCreateTickerAlert ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Create filing alert for ${ticker}`}
                  disabled={alertBusy}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCreateTickerAlert(ticker);
                  }}
                >
                  <Bell className={cn("h-3.5 w-3.5", alertBusy && "animate-pulse")} aria-hidden="true" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Open details for ${ticker}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onActivate(row.original.transactionId, event.currentTarget);
                  row.toggleSelected(true);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          );
        }
      }
    ],
    [
      alertPendingTicker,
      canCreateAlerts,
      canUseFavorites,
      density,
      favoriteTickerSet,
      onActivate,
      onCreateTickerAlert,
      onSortingChange,
      onToggleFavorite,
      sorting
    ]
  );

  const rowSelection = selectedTransactionId ? { [selectedTransactionId]: true } : {};
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.transactionId,
    state: {
      sorting,
      pagination,
      columnVisibility,
      columnOrder,
      columnPinning,
      rowSelection
    },
    manualSorting: true,
    manualPagination: true,
    rowCount: totalResultCount,
    autoResetPageIndex: false,
    enableMultiRowSelection: false,
    onSortingChange,
    onPaginationChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnPinningChange,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel()
  });
  const rowPresentationVersion = useMemo(
    () => ({}),
    [columns, columnOrder, columnPinning, columnVisibility, density]
  );
  const tableRows = table.getRowModel().rows;
  const estimatedRowHeight = ROW_HEIGHT_BY_DENSITY[density];
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: tableRows.length,
    estimateSize: () => estimatedRowHeight,
    getItemKey: (index) => tableRows[index]!.id,
    getScrollElement: () => scrollerRef.current,
    initialRect: VIRTUAL_INITIAL_RECT,
    measureElement: (element) =>
      element.getBoundingClientRect().height || estimatedRowHeight,
    overscan: VIRTUAL_ROW_OVERSCAN
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const tableRowsRef = useRef(tableRows);
  tableRowsRef.current = tableRows;
  const pendingRowFocusRef = useRef<PendingRowFocus | null>(null);
  const activeFocusRequestIdRef = useRef<number | null>(null);
  const finishPendingRowFocus = useCallback(
    (focused: boolean) => {
      const pending = pendingRowFocusRef.current;
      if (!pending) return;
      pendingRowFocusRef.current = null;
      if (pending.focusRequestId === null) {
        if (!focused) scrollerRef.current?.focus({ preventScroll: true });
        return;
      }
      onFocusRequestComplete(pending.focusRequestId, focused);
    },
    [onFocusRequestComplete, scrollerRef]
  );
  const settlePendingRowFocus = useCallback(
    (scroll: boolean) => {
      const pending = pendingRowFocusRef.current;
      if (!pending) return false;
      const targetIndex = tableRowsRef.current.findIndex(
        (row) => row.id === pending.transactionId
      );
      if (targetIndex < 0) {
        finishPendingRowFocus(false);
        return false;
      }
      if (scroll) {
        rowVirtualizer.scrollToIndex(targetIndex, { align: pending.align });
      }
      const target = scrollerRef.current?.querySelector<HTMLTableRowElement>(
        `tbody tr[data-index="${targetIndex}"]`
      );
      if (!target || target.dataset.transactionId !== pending.transactionId) return false;
      target.focus({ preventScroll: true });
      finishPendingRowFocus(document.activeElement === target);
      return document.activeElement === target;
    },
    [finishPendingRowFocus, rowVirtualizer, scrollerRef]
  );
  const startPendingRowFocus = useCallback(
    (pending: PendingRowFocus) => {
      pendingRowFocusRef.current = pending;
      settlePendingRowFocus(true);
    },
    [settlePendingRowFocus]
  );
  const navigateToRow = useCallback(
    (targetIndex: number, key: TableNavigationKey) => {
      const targetRow = tableRowsRef.current[targetIndex];
      if (!targetRow) return;
      startPendingRowFocus({
        align: key === "Home" ? "start" : key === "End" ? "end" : "auto",
        focusRequestId: null,
        transactionId: targetRow.id
      });
    },
    [startPendingRowFocus]
  );
  useLayoutEffect(() => {
    const pending = pendingRowFocusRef.current;
    if (pending === null) return;
    if (
      pending.focusRequestId !== null &&
      pending.focusRequestId !== focusRequest?.requestId
    ) {
      pendingRowFocusRef.current = null;
      return;
    }
    settlePendingRowFocus(true);
  }, [focusRequest, settlePendingRowFocus, tableRows, virtualRows]);
  useLayoutEffect(() => {
    if (focusRequest === null) {
      activeFocusRequestIdRef.current = null;
      const pending = pendingRowFocusRef.current;
      if (pending && pending.focusRequestId !== null) {
        pendingRowFocusRef.current = null;
      }
      return;
    }
    if (activeFocusRequestIdRef.current === focusRequest.requestId) return;
    activeFocusRequestIdRef.current = focusRequest.requestId;
    startPendingRowFocus({
      align: "auto",
      focusRequestId: focusRequest.requestId,
      transactionId: focusRequest.transactionId
    });
  }, [focusRequest, startPendingRowFocus]);
  const previousDensityRef = useRef(density);
  useLayoutEffect(() => {
    if (previousDensityRef.current === density) return;
    previousDensityRef.current = density;
    rowVirtualizer.measure();
  }, [density, rowVirtualizer]);
  const firstVirtualRow = virtualRows[0];
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const paddingBefore = firstVirtualRow?.start ?? 0;
  const paddingAfter = lastVirtualRow
    ? Math.max(0, rowVirtualizer.getTotalSize() - lastVirtualRow.end)
    : 0;
  const visibleColumnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="min-h-0 overflow-hidden rounded-md border border-border bg-surface shadow-sm">
      <div
        ref={scrollerRef}
        data-testid="insider-table-scroller"
        className="max-h-[calc(100vh-310px)] min-h-[360px] max-w-full overflow-auto"
        tabIndex={-1}
      >
        <table
          className={cn(
            "min-w-full border-collapse text-left",
            density === "compact" ? "text-xs" : "text-[13px]"
          )}
          style={{ width: Math.max(table.getTotalSize(), 960) }}
          aria-label="Insider trades"
          aria-rowcount={tableRows.length + 1}
          data-density={density}
          data-result-count={totalResultCount}
        >
          <thead className="sticky top-0 z-20 bg-surface-elevated text-[11px] uppercase text-muted-foreground">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} aria-rowindex={1}>
                {headerGroup.headers.map((header) => {
                  const sortIndex = sorting.findIndex((sort) => sort.id === header.column.id);
                  const isSortable = SORTABLE_COLUMN_IDS.has(header.column.id as ScreenerSortField);
                  const ariaSort =
                    sortIndex === 0
                      ? sorting[0]!.desc
                        ? "descending"
                        : "ascending"
                      : sortIndex < 0 && isSortable
                        ? "none"
                        : undefined;
                  const pinned = header.column.getIsPinned();
                  return (
                    <th
                      key={header.id}
                      data-column-id={header.column.id}
                      data-pinned={pinned || undefined}
                      aria-sort={ariaSort}
                      className="border-b border-border px-2 py-2 font-semibold"
                      style={pinnedStyle(header.column, true)}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            <tr
              aria-hidden="true"
              data-virtual-spacer="before"
              style={{ display: paddingBefore === 0 ? "none" : undefined }}
            >
              <td
                colSpan={visibleColumnCount}
                style={{ border: 0, height: paddingBefore, padding: 0 }}
              />
            </tr>
            {virtualRows.map((virtualRow) => {
              const row = tableRows[virtualRow.index]!;
              return (
                <InsiderTableBodyRow
                  key={row.id}
                  row={row}
                  selected={row.getIsSelected()}
                  density={density}
                  virtualIndex={virtualRow.index}
                  logicalRowCount={tableRows.length}
                  measureElement={rowVirtualizer.measureElement}
                  onActivate={onActivate}
                  onNavigate={navigateToRow}
                  onToggleFavorite={onToggleFavorite}
                  presentationVersion={rowPresentationVersion}
                />
              );
            })}
            <tr
              aria-hidden="true"
              data-virtual-spacer="after"
              style={{ display: paddingAfter === 0 ? "none" : undefined }}
            >
              <td
                colSpan={visibleColumnCount}
                style={{ border: 0, height: paddingAfter, padding: 0 }}
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

export function columnLabel(columnId: string): string {
  return SCREENER_COLUMN_LABELS[columnId as keyof typeof SCREENER_COLUMN_LABELS] ?? columnId;
}
